export const DEFAULT_TASK_SECTION_REFS = [
  'triage',
  'to_do',
  'in_progress',
  'blocked',
  'escalated',
  'done',
] as const;

export type TaskSectionRef = (typeof DEFAULT_TASK_SECTION_REFS)[number];

export type TaskPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export type TaskDashboardUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_image: string | null;
  job_title: string | null;
  display_name?: string | null;
};

export type Task = {
  id: number;
  organisation_id?: number;
  title: string;
  description: string | null;
  section_id: number;
  assigned_dashboard_user_id: number | null;
  assigned_dashboard_user_ids?: number[];
  created_by_user_id?: number | null;
  priority: TaskPriority;
  due_at: string | null;
  source_type: string | null;
  source_id: number | string | null;
  source_url: string | null;
  sort_order: number;
  completed_at: string | null;
  archived_at: string | null;
  created_at?: string;
  updated_at?: string;
  tags?: string[] | null;
  attachments?: TaskAttachment[] | null;
  comments?: TaskComment[] | null;
  activity?: TaskActivity[] | null;
};

export type TaskAttachment = {
  id?: string | number;
  name: string;
  url?: string | null;
  mime_type?: string | null;
  size?: number | null;
  created_at?: string;
};

export type TaskSection = {
  id: number;
  organisation_id: number;
  name: string;
  ref: TaskSectionRef | string;
  position: number;
  terminal: boolean;
  active: boolean;
  tasks?: Task[];
  count?: number;
};

export type TaskCounts = {
  total: number;
  overdue: number;
  low: number;
  normal: number;
  high: number;
  urgent: number;
};

export type TaskBoard = {
  board_type?: 'user' | 'organisation' | string;
  dashboard_user: TaskDashboardUser | null;
  sections: TaskSection[];
  counts: TaskCounts;
};

export type TaskBoardsMeta = {
  all: boolean;
  totalBoards: number;
  totalTasks: number;
};

export type TaskBoardsResponse = {
  data: TaskBoard[];
  meta: TaskBoardsMeta;
  message?: string;
};

export type GetTaskBoardsParams = {
  all?: boolean;
  dashboard_user_id?: number;
};

export type CreateTaskDto = {
  title: string;
  description?: string | null;
  section_id?: number;
  section_ref?: TaskSectionRef | string;
  assigned_dashboard_user_ids?: number[];
  priority?: TaskPriority;
  due_at?: string | null;
  tags?: string[];
  source_type?: string | null;
  source_id?: number | string | null;
  source_url?: string | null;
};

export type UpdateTaskDto = Partial<
  Pick<
    Task,
    | 'title'
    | 'description'
    | 'priority'
    | 'due_at'
    | 'tags'
    | 'source_type'
    | 'source_id'
    | 'source_url'
  >
>;

export type MoveTaskDto = {
  section_id?: number;
  section_ref?: TaskSectionRef | string;
  assigned_dashboard_user_ids?: number[];
  sort_order?: number;
};

export type TaskComment = {
  id: number;
  task_id: number;
  organisation_id: number;
  actor_dashboard_user_id: number;
  action?: 'comment' | string;
  comment: string;
  from_section_id?: number | null;
  to_section_id?: number | null;
  from_assigned_dashboard_user_id?: number | null;
  to_assigned_dashboard_user_id?: number | null;
  old_value?: unknown;
  new_value?: unknown;
  created_at: string;
};

export type TaskActivity = {
  id: number;
  task_id: number;
  organisation_id: number;
  actor_dashboard_user_id: number;
  action: string;
  comment: string | null;
  from_section_id?: number | null;
  to_section_id?: number | null;
  from_assigned_dashboard_user_id?: number | null;
  to_assigned_dashboard_user_id?: number | null;
  old_value?: unknown;
  new_value?: unknown;
  created_at: string;
};

export type CreateTaskCommentDto = {
  comment: string;
};

export type UpdateTaskCommentDto = {
  comment: string;
};

export type KanbanState = {
  isLoading: boolean;
  error: Error | string | null;
  board: KanbanBoard;
};

export type CardComment = {
  id: string;
  avatar: string;
  name: string;
  createdAt: Date | string | number;
  messageType: 'image' | 'text';
  message: string;
};

export type Assignee = {
  id: string;
  avatar: string;
  name: string;
};

export type KanbanCard = {
  id: string;
  name: string;
  description?: string;
  assignee: Assignee[];
  due: [Date | null, Date | null];
  attachments: string[];
  comments: CardComment[];
  completed: boolean;
};

export type KanbanColumn = {
  id: string;
  name: string;
  cardIds: string[];
  board?: KanbanBoard;
};

export type KanbanBoard = {
  cards: Record<string, KanbanCard>;
  columns: Record<string, KanbanColumn>;
  columnOrder: string[];
};
